/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { defaultPreText } from 'model/abilities/AbilityDefaults';
import AttributeValues from 'model/stats/AttributeValues';
import { MonsterOpen5e } from 'model/formats/MonsterOpen5e';
import { NamedKey, Open5eV2Document } from 'model/formats/Open5eV2';
import Speed from 'model/stats/Speed';

export interface CreatureSpeed {
    unit:    string;
    walk:    number;
    crawl:   number;
    hover:   boolean;
    fly:     number;
    burrow:  number;
    climb:   number;
    swim:    number;
}

export interface CreatureSavingThrows {
    strength?:     number;
    dexterity?:    number;
    constitution?: number;
    intelligence?: number;
    wisdom?:       number;
    charisma?:     number;
}

export interface CreatureAttack {
    name:                   string;
    attack_type:            string;
    to_hit_mod:             number;
    reach:                  number | null;
    range:                  number | null;
    long_range:             number | null;
    target_creature_only:   boolean;
    damage_die_count:       number;
    damage_die_type:        string;
    damage_bonus:           number | null;
    damage_type:            NamedKey | null;
    extra_damage_die_count: number | null;
    extra_damage_die_type:  string | null;
    extra_damage_bonus:     number | null;
    extra_damage_type:      NamedKey | null;
    distance_unit:          string;
}

export type CreatureActionType =
    | 'ACTION'
    | 'BONUS_ACTION'
    | 'REACTION'
    | 'LEGENDARY_ACTION'
    | 'MYTHIC_ACTION'
    | 'VILLAIN_ACTION'
    | 'LAIR_ACTION';

export interface CreatureAction {
    name:                  string;
    desc:                  string;
    attacks:               CreatureAttack[];
    action_type:           CreatureActionType;
    order_in_statblock:    number;
    legendary_action_cost: number | null;
    limited_to_form:       string | null;
    usage_limits:          any | null;
}

export interface CreatureResistancesAndImmunities {
    damage_immunities_display:      string;
    damage_immunities:              NamedKey[];
    damage_resistances_display:     string;
    damage_resistances:             NamedKey[];
    damage_vulnerabilities_display: string;
    damage_vulnerabilities:         NamedKey[];
    condition_immunities_display:   string;
    condition_immunities:           NamedKey[];
}

export interface CreatureLanguages {
    as_string: string;
    data:      (NamedKey & { desc: string })[];
}

class CreatureOpen5e {
    key:                        string = "";
    name:                       string = "Default Creature";
    document:                   Open5eV2Document = { name: "", key: "", type: "", display_name: "", publisher: { name: "", key: "" }, gamesystem: { name: "", key: "" }, permalink: "" };
    type:                       NamedKey = { name: "Humanoid", key: "humanoid" };
    size:                       NamedKey = { name: "Medium",   key: "medium" };
    challenge_rating:           number = 1.0;
    proficiency_bonus:          number | null = null;
    speed:                      Partial<CreatureSpeed> & { walk: number; unit: string } = { walk: 30, unit: "feet" };
    speed_all:                  CreatureSpeed = { unit: "feet", walk: 30, crawl: 15, hover: false, fly: 0, burrow: 0, climb: 0, swim: 0 };
    category:                   string = "Monsters";
    subcategory:                string | null = null;
    alignment:                  string = "";
    languages:                  CreatureLanguages = { as_string: "", data: [] };
    armor_class:                number = 10;
    armor_detail:               string = "";
    hit_points:                 number = 5;
    hit_dice:                   string | null = "1d8";
    experience_points:          number = 0;
    ability_scores:             AttributeValues = { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10 };
    modifiers:                  AttributeValues = { strength: 0,  dexterity: 0,  constitution: 0,  intelligence: 0,  wisdom: 0,  charisma: 0  };
    initiative_bonus:           number = 0;
    saving_throws:              CreatureSavingThrows = {};
    saving_throws_all:          AttributeValues = { strength: 0,  dexterity: 0,  constitution: 0,  intelligence: 0,  wisdom: 0,  charisma: 0  };
    skill_bonuses:              Record<string, number> = {};
    skill_bonuses_all:          Record<string, number> = {};
    passive_perception:         number = 10;
    resistances_and_immunities: CreatureResistancesAndImmunities = {
        damage_immunities_display:      "",
        damage_immunities:              [],
        damage_resistances_display:     "",
        damage_resistances:             [],
        damage_vulnerabilities_display: "",
        damage_vulnerabilities:         [],
        condition_immunities_display:   "",
        condition_immunities:           [],
    };
    normal_sight_range:         number = 0;
    darkvision_range:           number | null = null;
    blindsight_range:           number | null = null;
    tremorsense_range:          number | null = null;
    truesight_range:            number | null = null;
    actions:                    CreatureAction[] = [];
    traits:                     { name: string; desc: string }[] = [];
    creaturesets:               any[] = [];
    environments:               (string | NamedKey)[] = [];
    illustration:               string | null = null;

    get slug(): string { return this.key; }

    static convertToMonsterOpen5e(c: CreatureOpen5e): MonsterOpen5e {
        const m = new MonsterOpen5e();

        m.slug                   = c.key;
        m.name                   = c.name;
        m.size                   = c.size.name;
        m.type                   = c.type.name;
        m.subtype                = '';
        m.alignment              = c.alignment;
        m.armor_class            = c.armor_class;
        m.armor_desc             = c.armor_detail;
        m.hit_points             = c.hit_points;
        m.hit_dice               = c.hit_dice ?? '';
        m.strength               = c.ability_scores.strength;
        m.dexterity              = c.ability_scores.dexterity;
        m.constitution           = c.ability_scores.constitution;
        m.intelligence           = c.ability_scores.intelligence;
        m.wisdom                 = c.ability_scores.wisdom;
        m.charisma               = c.ability_scores.charisma;
        m.strength_save          = c.saving_throws.strength     ?? null;
        m.dexterity_save         = c.saving_throws.dexterity    ?? null;
        m.constitution_save      = c.saving_throws.constitution ?? null;
        m.intelligence_save      = c.saving_throws.intelligence ?? null;
        m.wisdom_save            = c.saving_throws.wisdom       ?? null;
        m.charisma_save          = c.saving_throws.charisma     ?? null;
        m.perception             = c.skill_bonuses_all['perception'] ?? (c.passive_perception - 10);
        const titleCase = (key: string) => key.split(' ').map(w => w.length > 2 ? w.charAt(0).toUpperCase() + w.substring(1) : w).join(' ');
        m.skills = new Map(Object.entries(c.skill_bonuses).map(([key, val]) => [titleCase(key), val]));
        m.skills.set('initiative', c.initiative_bonus);
        m.damage_vulnerabilities = c.resistances_and_immunities.damage_vulnerabilities_display;
        m.damage_resistances     = c.resistances_and_immunities.damage_resistances_display;
        m.damage_immunities      = c.resistances_and_immunities.damage_immunities_display;
        m.condition_immunities   = c.resistances_and_immunities.condition_immunities_display;
        m.languages              = c.languages.as_string;
        m.challenge_rating       = CreatureOpen5e.crString(c.challenge_rating);
        m.cr                     = c.challenge_rating;
        m.environments           = (c.environments || []).map((e: any) => typeof e === 'string' ? e : (e?.name ?? String(e)));
        m.img_main               = c.illustration;
        m.document__slug         = c.document.key;
        m.document__title        = c.document.display_name;
        m.document__license_url  = '';
        m.document__url          = c.document.permalink;
        m.useMm2024              = c.document.key === 'srd-2024';

        const speed = new Speed();
        speed.walk   = c.speed_all.walk;
        speed.burrow = c.speed_all.burrow;
        speed.swim   = c.speed_all.swim;
        speed.climb  = c.speed_all.climb;
        speed.fly    = c.speed_all.fly;
        speed.hover  = c.speed_all.hover;
        m.speed = speed;

        const senses: string[] = [];
        if (c.darkvision_range)   senses.push(`darkvision ${c.darkvision_range} ft.`);
        if (c.blindsight_range)   {
            let blindSight = `blindsight ${c.blindsight_range} ft.`;
            if (!c.normal_sight_range) {
                blindSight += ' (blind beyond this radius)';
            }
            senses.push(blindSight);
        }
        if (c.tremorsense_range)  senses.push(`tremorsense ${c.tremorsense_range} ft.`);
        if (c.truesight_range)    senses.push(`truesight ${c.truesight_range} ft.`);
        senses.push(`passive Perception ${c.passive_perception}`);
        m.senses = senses.join(', ');

        const toAbility = (a: CreatureAction): Ability => {
            const ability = { name: a.name, desc: a.desc };
            const notes: string[] = [];
            if (a.legendary_action_cost && a.legendary_action_cost > 1) {
                notes.push(`Costs ${a.legendary_action_cost} Actions`);
            }
            if (a.usage_limits?.type == 'PER_DAY' && a.usage_limits.param) {
                notes.push(`${a.usage_limits.param}/day`);
            }
            if (a.limited_to_form ) {
                notes.push(a.limited_to_form);
            }
            if (a.usage_limits?.type == 'RECHARGE_ON_ROLL' && a.usage_limits.param) {
                notes.push(`Recharge ${a.usage_limits.param}${(a.usage_limits.param < 6) ? '-6' : ''}`);
            }
            if (notes.length > 0) {
                ability.name += ` (${notes.join(', ')})`;
            }
            return ability;
        }
        c.actions.sort((a,b) => {return a.order_in_statblock - b.order_in_statblock});
        m.actions           = c.actions.filter(a => a.action_type === 'ACTION').map(toAbility);
        m.bonus_actions     = c.actions.filter(a => a.action_type === 'BONUS_ACTION').map(toAbility);
        m.reactions         = c.actions.filter(a => a.action_type === 'REACTION').map(toAbility);
        // Villain actions have no dedicated section, so fold them into legendary actions rather than
        // dropping them. Lair and mythic actions get their own arrays (see Monster.lair_actions etc.).
        m.legendary_actions = c.actions.filter(a => a.action_type === 'LEGENDARY_ACTION' || a.action_type === 'VILLAIN_ACTION').map(toAbility);
        m.mythic_actions    = c.actions.filter(a => a.action_type === 'MYTHIC_ACTION').map(toAbility);
        m.lair_actions      = c.actions.filter(a => a.action_type === 'LAIR_ACTION').map(toAbility);
        m.special_abilities = c.traits.map(t => ({ name: t.name, desc: t.desc }));

        if (m.legendary_actions?.length > 0) {
            // This is not needed, because the new V2 creatures have an action where the V1 Monsters needed the legendary_desc.
            m.legendary_desc = defaultPreText['Legendary Actions'];
        }
        return m;
    }

    private static crString(cr: number): string {
        switch (cr) {
            case 0.125: return '1/8';
            case 0.25:  return '1/4';
            case 0.5:   return '1/2';
            default:    return String(cr);
        }
    }
}

export default CreatureOpen5e;
